import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { CalendarCheck, Compass, Map, Wallet, Luggage } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@tappa/ui';

/**
 * Navigazione principale: cinque voci, non una di piu' (brief §6).
 *
 * `aria-current="page"` sulla voce attiva: lo stato non e' veicolato dal solo colore.
 * Area tap 48px minimo, safe-area rispettata su iPhone.
 */

interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: LucideIcon;
}

const ITEMS: readonly NavItem[] = [
  { to: '/oggi', label: 'Oggi', icon: CalendarCheck },
  { to: '/mappa', label: 'Mappa', icon: Map },
  { to: '/scopri', label: 'Scopri', icon: Compass },
  { to: '/budget', label: 'Budget', icon: Wallet },
  { to: '/viaggio', label: 'Viaggio', icon: Luggage },
];

export function BottomNav(): ReactNode {
  return (
    <nav
      aria-label="Navigazione principale"
      className="safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface/95 backdrop-blur"
    >
      <ul className="mx-auto flex max-w-[var(--content-max-width)] items-stretch">
        {ITEMS.map((item) => (
          <li key={item.to} className="flex-1">
            {/* NavLink imposta da solo aria-current="page" sulla voce attiva. */}
            <NavLink
              to={item.to}
              // Aggancio per la guida al primo accesso: evidenzia la voce di cui
              // sta parlando, cosi' si impara dove tornare a cercarla.
              data-tour-nav={item.to}
              className={({ isActive }) =>
                cn(
                  'flex min-h-[60px] flex-col items-center justify-center gap-1 px-1 py-2',
                  'text-[11.5px] transition-colors duration-[var(--dur-fast)]',
                  isActive ? 'font-semibold text-brand-strong' : 'text-text-muted',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    size={22}
                    aria-hidden="true"
                    strokeWidth={isActive ? 2.4 : 1.8}
                  />
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
